import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import { recordCardSnapshot } from '../lib/cardSnapshots'
import type { Card } from '../types'

// ── Fill (Prompt 2 backend) ──────────────────────────────────────────────────

export interface FillFieldAttribution {
  field: string
  tier: 'corpus' | 'external' | 'ai'
  source: string
}

export interface FillCardResponse {
  card: Card
  filled_fields: FillFieldAttribution[]
  ai_call_made: boolean
  ai_budget_exhausted: boolean
}

export function useFillCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (cardId: string) => {
      const { data } = await api.post<FillCardResponse>(`/api/v1/cards/${cardId}/fill`)
      // Fill writes the card server-side, so its response is a newer answer
      // than anything the list holds — recorded for the same reason every other
      // card write's is (lib/cardSnapshots).
      recordCardSnapshot(data.card)
      return data
    },
    onSuccess: ({ card }) => {
      // Same invalidation rule every card mutation follows — keeps card_count
      // and the deck-scoped card list fresh (CLAUDE.md coding_rules #14).
      qc.invalidateQueries({ queryKey: ['cards', card.deck_id] })
      qc.invalidateQueries({ queryKey: ['deck', card.deck_id] })
      qc.invalidateQueries({ queryKey: ['decks'] })
      // Fill can now establish a card's corpus linkage where it had none
      // (fill_service records the entry it resolved from), so what
      // `/cards/{id}/senses` would answer has changed — and this query has a
      // 60s staleTime, which is exactly long enough to serve a stale "no
      // senses" to a reader who just watched Fill populate the card.
      qc.invalidateQueries({ queryKey: ['card-senses', card.id] })
    },
  })
}

// ── Meaning arrows (Prompt 3 backend) ────────────────────────────────────────

export interface CardSenseCandidate {
  word_entry_id: string
  lemma: string
  language: string
  pos: string | null
  definition: string | null
  example: string | null
  translation: string | null
  sense_id: string | null
  is_selected: boolean
}

export interface CardSensesResponse {
  card_id: string
  term: string
  language: string
  total: number
  selected_word_entry_id: string | null
  candidates: CardSenseCandidate[]
  /** Corpus rows the server folded away as indistinguishable from a candidate
   * it kept (same part of speech, same definition — ADR-065 write-back copies).
   * Deliberately not rendered: the reader gets distinct meanings, and this is
   * the data-quality signal that says how much duplication that took. */
  duplicates_collapsed: number
}

export function useCardSenses(cardId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['card-senses', cardId],
    queryFn: async () => {
      const { data } = await api.get<CardSensesResponse>(`/api/v1/cards/${cardId}/senses`)
      return data
    },
    enabled,
    staleTime: 60_000,
  })
}

export function useSwitchCardSense() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ cardId, wordEntryId }: { cardId: string; wordEntryId: string | null }) => {
      const { data } = await api.patch<CardSensesResponse>(
        `/api/v1/cards/${cardId}/sense`,
        { word_entry_id: wordEntryId },
      )
      return data
    },
    onSuccess: (data, { cardId }) => {
      qc.setQueryData(['card-senses', cardId], data)
    },
  })
}
